import React, { memo, useCallback, useMemo } from "react";
import DateRangePicker from "@ui/DateRangePicker/DateRangePicker";
import { type DateValue, parseAbsolute } from "@internationalized/date";
import { type Project } from "../../../../../../../api/useProjects";
import type { PeriodT } from "../../../../schema/period";
import type { RangeValue } from "@react-types/shared";
import { baseAggregationPeriodPresets } from "../../../../../../../domain/aggregation/preset";
import useGroups from "../../../../../../../api/useGroups";

export const PeriodSelector = memo(
  ({
    project,
    value,
    label,
    defaultValue,
    onChange,
    maxPeriod,
  }: {
    project: Project;
    value: PeriodT;
    label?: string;
    defaultValue: { start: DateValue; end: DateValue } | null;
    onChange: (value: { from: number; to: number }) => void;
    maxPeriod: { from: number; to: number };
  }) => {
    const from = useMemo(
      () => parseAbsolute(new Date(maxPeriod.from).toISOString(), project.timezone),
      [maxPeriod, project],
    );

    const to = useMemo(
      () => parseAbsolute(new Date(maxPeriod.to).toISOString(), project.timezone),
      [maxPeriod, project],
    );

    const groups = useGroups(project.id);
    const presets = useMemo(
      () =>
        baseAggregationPeriodPresets({
          timezone: project.timezone,
          groups,
          limit: {
            from: new Date(maxPeriod.from),
            to: new Date(maxPeriod.to),
          },
        }),
      [project.timezone, maxPeriod.from, maxPeriod.to, groups],
    );

    const selectedPeriod = useMemo(() => {
      if (!value) return;

      if (typeof value === "string") {
        const presetValue = presets.find(preset => preset.metadata?.["id"] === value);
        if (presetValue) {
          return {
            start:
              typeof presetValue.startDate === "function"
                ? presetValue.startDate()
                : presetValue.startDate,
            end:
              typeof presetValue.endDate === "function"
                ? presetValue.endDate()
                : presetValue.endDate,
          };
        }
      } else {
        let from = new Date(maxPeriod.from).toISOString();
        let to = new Date(maxPeriod.to).toISOString();

        if (value.from && value.to) {
          from = new Date(value.from).toISOString();
          to = new Date(value.to).toISOString();
        } else if (defaultValue) {
          from = defaultValue.start.toDate(project.timezone).toISOString();
          to = defaultValue.end.toDate(project.timezone).toISOString();
        }

        return {
          start: parseAbsolute(from, project.timezone),
          end: parseAbsolute(to, project.timezone),
        };
      }
      return;
    }, [value, defaultValue, presets, maxPeriod.from, maxPeriod.to, project.timezone]);

    const isDateUnavailable = useCallback(
      (date: DateValue) => {
        if (from && date.compare(from) < 0) return true;
        if (to && date.compare(to) > 0) return true;
        return false;
      },
      [from, to],
    );

    const setSelectedPeriod = useCallback(
      (value: RangeValue<DateValue> | null) => {
        if (value) {
          onChange({
            from: value.start
              .set({ hour: 0, minute: 0, second: 0 })
              .toDate(project.timezone)
              .getTime(),
            to: value.end
              .set({ hour: 23, minute: 59, second: 59 })
              .toDate(project.timezone)
              .getTime(),
          });
        } else if (defaultValue) {
          onChange({
            from: defaultValue.start
              .set({ hour: 0, minute: 0, second: 0 })
              .toDate(project.timezone)
              .getTime(),
            to: defaultValue.end
              .set({ hour: 23, minute: 59, second: 59 })
              .toDate(project.timezone)
              .getTime(),
          });
        }
      },
      [defaultValue, project.timezone, onChange],
    );

    return (
      <DateRangePicker
        id=""
        popoverPlacement="bottom end"
        label={label}
        presets={presets}
        dateOptions={{
          shouldCloseOnSelect: true,
          hideTimeZone: true,
          granularity: "day",
          minValue: from,
          maxValue: to,
          value: selectedPeriod,
          isDateUnavailable,
          onChange: setSelectedPeriod,
        }}
      />
    );
  },
);
